import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiCpu, FiEye, FiPlay, FiRefreshCw, FiSave, FiShield, FiTool, FiXCircle, FiZap } from 'react-icons/fi';
import { listInstances, CommandCentreInstance } from '../../api/instances';
import {
  EzeeAiModel,
  EzeeAiProviderSettings,
  getEzeeAiSettings,
  listEzeeAiModels,
  saveEzeeAiSettings,
  testEzeeAiConnection,
  EzeeNervousSystemState,
  getEzeeNervousSystemState,
  updateEzeeNervousSystemState,
  EzeeKnowledgeStatus,
  getEzeeKnowledgeStatus,
  syncEzeeKnowledge,
  EzeeDomainEventQueueStatus,
  EzeeEvaluationCase,
  EzeeEvaluationRun,
  EzeeImprovementAutonomySettings,
  EzeeImprovementDashboard,
  EzeeImprovementObservation,
  EzeeImprovementRecommendation,
  getEzeeImprovementAutonomy,
  getEzeeImprovementDashboard,
  listEzeeEvaluationCases,
  listEzeeEvaluationRuns,
  listEzeeImprovementObservations,
  listEzeeImprovementRecommendations,
  prepareEzeeImprovementRecommendation,
  reviewEzeeImprovementRecommendation,
  runEzeeEvaluationSuite,
  runEzeeImprovementCycle,
  simulateEzeeImprovementRecommendation,
  updateEzeeImprovementAutonomy,
  verifyEzeeImprovementRecommendation,
} from '../../api/ezeeAi';
import './EzeeAiAdminPage.css';

const DIGITALOCEAN_BASE_URL = 'https://inference.do-ai.run/v1';

const EMPTY_EVENT_QUEUE: EzeeDomainEventQueueStatus = {
  pending: 0,
  processing: 0,
  retry: 0,
  failed: 0,
  latestEvent: null,
};

const EMPTY_KNOWLEDGE_STATUS: EzeeKnowledgeStatus = {
  documents: 0,
  enabledDocuments: 0,
  chunks: 0,
  latestIndexedAt: null,
};

const EMPTY_NERVOUS_SYSTEM: EzeeNervousSystemState = {
  enabled: false,
  emergencyStop: false,
  reason: null,
  stoppedAt: null,
  stoppedByUserId: null,
  resumedAt: null,
  resumedByUserId: null,
};

const EMPTY_IMPROVEMENT_AUTONOMY: EzeeImprovementAutonomySettings = {
  mode: 'observe',
  minRecommendationOccurrences: 3,
  minAutoSafeConfidence: 0.95,
  allowedSafeActions: [],
  reflectionEnabled: false,
  evaluationsEnabled: false,
};

const EMPTY_IMPROVEMENT_DASHBOARD: EzeeImprovementDashboard = {
  autonomy: EMPTY_IMPROVEMENT_AUTONOMY,
  openObservations: 0,
  recommendationCount: 0,
  recommendationsByStatus: {},
  readyForReview: 0,
  verified: 0,
  regressed: 0,
  acceptanceRate: null,
  verificationSuccessRate: null,
  healthScore: null,
  evalCases: 0,
  latestEvaluation: null,
};

const EMPTY_SETTINGS: EzeeAiProviderSettings = {
  provider: 'digitalocean',
  mode: 'live',
  testApiKey: '',
  liveApiKey: '',
  testModel: '',
  liveModel: '',
  baseUrl: DIGITALOCEAN_BASE_URL,
  hasTestApiKey: false,
  hasLiveApiKey: false,
  testApiKeyMasked: '',
  liveApiKeyMasked: '',
  useEnvFallback: true,
};

type Notice = { tone: 'success' | 'error' | 'info'; message: string } | null;

const EzeeAiAdminPage: React.FC = () => {
  const [instances, setInstances] = useState<CommandCentreInstance[]>([]);
  const [instanceUniqueId, setInstanceUniqueId] = useState('');
  const [settings, setSettings] = useState<EzeeAiProviderSettings>(EMPTY_SETTINGS);
  const [models, setModels] = useState<EzeeAiModel[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [nervousSystem, setNervousSystem] = useState<EzeeNervousSystemState>(EMPTY_NERVOUS_SYSTEM);
  const [runtimeArmed, setRuntimeArmed] = useState(false);
  const [backgroundWorkersArmed, setBackgroundWorkersArmed] = useState(false);
  const [nervousLoading, setNervousLoading] = useState(false);
  const [nervousSaving, setNervousSaving] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState('');
  const [knowledgeStatus, setKnowledgeStatus] = useState<EzeeKnowledgeStatus>(EMPTY_KNOWLEDGE_STATUS);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeSyncing, setKnowledgeSyncing] = useState(false);
  const [eventQueue, setEventQueue] = useState<EzeeDomainEventQueueStatus>(EMPTY_EVENT_QUEUE);
  const [improvementDashboard, setImprovementDashboard] = useState<EzeeImprovementDashboard>(EMPTY_IMPROVEMENT_DASHBOARD);
  const [improvementAutonomy, setImprovementAutonomy] = useState<EzeeImprovementAutonomySettings>(EMPTY_IMPROVEMENT_AUTONOMY);
  const [recommendations, setRecommendations] = useState<EzeeImprovementRecommendation[]>([]);
  const [evaluationCases, setEvaluationCases] = useState<EzeeEvaluationCase[]>([]);
  const [evaluationRuns, setEvaluationRuns] = useState<EzeeEvaluationRun[]>([]);
  const [evidenceByRecommendation, setEvidenceByRecommendation] = useState<Record<number, EzeeImprovementObservation[]>>({});
  const [reviewReasonByRecommendation, setReviewReasonByRecommendation] = useState<Record<number, string>>({});
  const [improvementLoading, setImprovementLoading] = useState(false);
  const [improvementSaving, setImprovementSaving] = useState(false);
  const [improvementActionId, setImprovementActionId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingInstances(true);
    listInstances()
      .then((rows) => {
        if (cancelled) return;
        const usable = rows.filter((row) => Boolean(row.instanceUniqueId));
        setInstances(usable);
        const preferred =
          usable.find((row) => row.instanceUniqueId === 'elite-promo-marketing') ||
          usable[0];
        if (preferred?.instanceUniqueId) {
          setInstanceUniqueId(preferred.instanceUniqueId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotice({ tone: 'error', message: 'Unable to load Elite Core instances.' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingInstances(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!instanceUniqueId) return;
    let cancelled = false;
    setLoadingSettings(true);
    setNotice(null);
    setModels([]);

    getEzeeAiSettings(instanceUniqueId)
      .then((value) => {
        if (!cancelled) {
          setSettings({
            ...EMPTY_SETTINGS,
            ...value,
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setNotice({
            tone: 'error',
            message: error?.message || 'Unable to load Ezee AI settings.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSettings(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    let cancelled = false;
    setNervousLoading(true);
    // Fail closed between tenants: never render the previous instance's power state.
    setNervousSystem({ ...EMPTY_NERVOUS_SYSTEM });
    setEventQueue({ ...EMPTY_EVENT_QUEUE });
    setEmergencyReason('');
    setRuntimeArmed(false);
    setBackgroundWorkersArmed(false);

    getEzeeNervousSystemState(instanceUniqueId)
      .then((response) => {
        if (cancelled) return;
        setNervousSystem({ ...EMPTY_NERVOUS_SYSTEM, ...(response.state || {}) });
        setRuntimeArmed(response.deployment?.runtimeArmed === true);
        setBackgroundWorkersArmed(response.deployment?.backgroundWorkersArmed === true);
        setEmergencyReason(response.state?.reason || '');
        setEventQueue({ ...EMPTY_EVENT_QUEUE, ...(response.eventQueue || {}) });
      })
      .catch((error) => {
        if (!cancelled) {
          setNotice({
            tone: 'error',
            message: error?.message || 'Unable to load the Ezee nervous system state.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setNervousLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    let cancelled = false;
    setKnowledgeLoading(true);

    getEzeeKnowledgeStatus(instanceUniqueId)
      .then((status) => {
        if (!cancelled) setKnowledgeStatus({ ...EMPTY_KNOWLEDGE_STATUS, ...(status || {}) });
      })
      .catch((error) => {
        if (!cancelled) {
          setNotice({
            tone: 'error',
            message: error?.message || 'Unable to load Ezee knowledge status.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setKnowledgeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    let cancelled = false;
    setImprovementLoading(true);

    Promise.all([
      getEzeeImprovementDashboard(instanceUniqueId),
      getEzeeImprovementAutonomy(instanceUniqueId),
      listEzeeImprovementRecommendations(instanceUniqueId),
      listEzeeEvaluationCases(instanceUniqueId),
      listEzeeEvaluationRuns(instanceUniqueId),
    ])
      .then(([dashboard, autonomy, recommendationRows, evalCaseRows, evalRunRows]) => {
        if (cancelled) return;
        setImprovementDashboard({ ...EMPTY_IMPROVEMENT_DASHBOARD, ...dashboard });
        setImprovementAutonomy({ ...EMPTY_IMPROVEMENT_AUTONOMY, ...autonomy });
        setRecommendations(recommendationRows || []);
        setEvaluationCases(evalCaseRows || []);
        setEvaluationRuns(evalRunRows || []);
        setEvidenceByRecommendation({});
      })
      .catch((error) => {
        if (!cancelled) {
          setNotice({
            tone: 'error',
            message: error?.message || 'Unable to load Ezee R&D.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setImprovementLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceUniqueId]);

  const selectedInstance = useMemo(
    () => instances.find((instance) => instance.instanceUniqueId === instanceUniqueId) || null,
    [instances, instanceUniqueId]
  );

  const activeModel = settings.mode === 'test' ? settings.testModel : settings.liveModel;
  const activeKeyMask =
    settings.mode === 'test' ? settings.testApiKeyMasked : settings.liveApiKeyMasked;

  const update = <K extends keyof EzeeAiProviderSettings>(
    key: K,
    value: EzeeAiProviderSettings[K]
  ) => {
    setSettings((previous) => ({ ...previous, [key]: value }));
  };

  const refreshImprovementData = async () => {
    if (!instanceUniqueId) return;
    const [dashboard, autonomy, recommendationRows, evalCaseRows, evalRunRows] = await Promise.all([
      getEzeeImprovementDashboard(instanceUniqueId),
      getEzeeImprovementAutonomy(instanceUniqueId),
      listEzeeImprovementRecommendations(instanceUniqueId),
      listEzeeEvaluationCases(instanceUniqueId),
      listEzeeEvaluationRuns(instanceUniqueId),
    ]);
    setImprovementDashboard({ ...EMPTY_IMPROVEMENT_DASHBOARD, ...dashboard });
    setImprovementAutonomy({ ...EMPTY_IMPROVEMENT_AUTONOMY, ...autonomy });
    setRecommendations(recommendationRows || []);
    setEvaluationCases(evalCaseRows || []);
    setEvaluationRuns(evalRunRows || []);
  };

  const handleRunImprovementCycle = async () => {
    if (!instanceUniqueId || improvementLoading) return;
    setImprovementLoading(true);
    setNotice(null);
    try {
      await runEzeeImprovementCycle(instanceUniqueId);
      await refreshImprovementData();
      setNotice({ tone: 'success', message: 'Ezee R&D reflection cycle completed.' });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to run Ezee R&D cycle.' });
    } finally {
      setImprovementLoading(false);
    }
  };

  const handleSaveImprovementAutonomy = async () => {
    if (!instanceUniqueId || improvementSaving) return;
    setImprovementSaving(true);
    setNotice(null);
    try {
      const settings = await updateEzeeImprovementAutonomy(instanceUniqueId, improvementAutonomy);
      setImprovementAutonomy({ ...EMPTY_IMPROVEMENT_AUTONOMY, ...settings });
      setNotice({ tone: 'success', message: 'Ezee R&D autonomy settings saved.' });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to save Ezee R&D autonomy.' });
    } finally {
      setImprovementSaving(false);
    }
  };

  const replaceRecommendation = (updated: EzeeImprovementRecommendation) => {
    setRecommendations((rows) =>
      rows.map((row) => (row.id === updated.id ? updated : row))
    );
  };

  const handleLoadEvidence = async (recommendation: EzeeImprovementRecommendation) => {
    if (!instanceUniqueId) return;
    try {
      const observations = await listEzeeImprovementObservations(instanceUniqueId, recommendation.fingerprint);
      setEvidenceByRecommendation((current) => ({ ...current, [recommendation.id]: observations }));
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to load recommendation evidence.' });
    }
  };

  const handleReviewRecommendation = async (
    recommendation: EzeeImprovementRecommendation,
    decision: 'approved' | 'rejected' | 'needs_changes' | 'implemented' | 'archived'
  ) => {
    if (!instanceUniqueId || improvementActionId) return;
    setImprovementActionId(recommendation.id);
    setNotice(null);
    try {
      const updated = await reviewEzeeImprovementRecommendation(
        instanceUniqueId,
        recommendation.id,
        {
          decision,
          reason: reviewReasonByRecommendation[recommendation.id] || undefined,
        }
      );
      replaceRecommendation(updated);
      await refreshImprovementData();
      setNotice({ tone: 'success', message: `Recommendation marked ${decision.replace('_', ' ')}.` });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to review recommendation.' });
    } finally {
      setImprovementActionId(null);
    }
  };

  const handlePrepareRecommendation = async (recommendation: EzeeImprovementRecommendation) => {
    if (!instanceUniqueId || improvementActionId) return;
    setImprovementActionId(recommendation.id);
    try {
      const updated = await prepareEzeeImprovementRecommendation(instanceUniqueId, recommendation.id);
      replaceRecommendation(updated);
      setNotice({ tone: 'success', message: 'Exact implementation proposal prepared for review.' });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to prepare recommendation.' });
    } finally {
      setImprovementActionId(null);
    }
  };

  const handleSimulateRecommendation = async (recommendation: EzeeImprovementRecommendation) => {
    if (!instanceUniqueId || improvementActionId) return;
    setImprovementActionId(recommendation.id);
    try {
      await simulateEzeeImprovementRecommendation(instanceUniqueId, recommendation.id);
      await refreshImprovementData();
      setNotice({ tone: 'success', message: 'Historical recommendation simulation completed.' });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to simulate recommendation.' });
    } finally {
      setImprovementActionId(null);
    }
  };

  const handleVerifyRecommendation = async (recommendation: EzeeImprovementRecommendation) => {
    if (!instanceUniqueId || improvementActionId) return;
    setImprovementActionId(recommendation.id);
    try {
      await verifyEzeeImprovementRecommendation(instanceUniqueId, recommendation.id);
      await refreshImprovementData();
      setNotice({ tone: 'success', message: 'Post-implementation verification completed.' });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to verify recommendation.' });
    } finally {
      setImprovementActionId(null);
    }
  };

  const handleRunEvaluations = async (recommendationId?: number) => {
    if (!instanceUniqueId || improvementLoading) return;
    setImprovementLoading(true);
    try {
      const run = await runEzeeEvaluationSuite(instanceUniqueId, recommendationId);
      setEvaluationRuns((rows) => [run, ...rows.filter((row) => row.id !== run.id)]);
      await refreshImprovementData();
      setNotice({
        tone: run.failedCases ? 'info' : 'success',
        message: `Ezee evals complete: ${run.passedCases}/${run.totalCases} passed.`,
      });
    } catch (error: any) {
      setNotice({ tone: 'error', message: error?.message || 'Unable to run Ezee evaluations.' });
    } finally {
      setImprovementLoading(false);
    }
  };

  const handleProviderChange = (provider: string) => {
    setModels([]);
    setSettings((previous) => ({
      ...previous,
      provider,
      baseUrl:
        provider === 'digitalocean'
          ? DIGITALOCEAN_BASE_URL
          : previous.provider === 'digitalocean'
            ? ''
            : previous.baseUrl,
      testModel: provider === previous.provider ? previous.testModel : '',
      liveModel: provider === previous.provider ? previous.liveModel : '',
    }));
  };

  const handleSave = async () => {
    if (!instanceUniqueId) return;
    setSaving(true);
    setNotice(null);
    try {
      const saved = await saveEzeeAiSettings(instanceUniqueId, {
        provider: settings.provider,
        mode: settings.mode,
        testApiKey: settings.testApiKey,
        liveApiKey: settings.liveApiKey,
        testModel: settings.testModel,
        liveModel: settings.liveModel,
        baseUrl: settings.baseUrl,
        useEnvFallback: settings.useEnvFallback,
      });
      setSettings({ ...EMPTY_SETTINGS, ...saved });
      setNotice({ tone: 'success', message: 'Ezee AI provider settings saved.' });
    } catch (error: any) {
      setNotice({
        tone: 'error',
        message: error?.message || 'Unable to save Ezee AI settings.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLoadModels = async () => {
    if (!instanceUniqueId) return;
    setLoadingModels(true);
    setNotice(null);
    try {
      const response = await listEzeeAiModels(instanceUniqueId);
      setModels(response.models || []);
      setNotice({
        tone: 'success',
        message: `Loaded ${response.models?.length || 0} provider models.`,
      });
    } catch (error: any) {
      setNotice({
        tone: 'error',
        message:
          error?.message ||
          'Unable to load provider models. Save a valid provider key first.',
      });
    } finally {
      setLoadingModels(false);
    }
  };

  const setEmergencyStop = async (emergencyStop: boolean) => {
    if (!instanceUniqueId || nervousLoading || nervousSaving) return;

    if (emergencyStop && !emergencyReason.trim()) {
      setNotice({
        tone: 'error',
        message: 'Add a short reason before enabling the emergency stop.',
      });
      return;
    }

    setNervousSaving(true);
    setNotice(null);
    try {
      // Clearing the emergency stop never resumes operations implicitly.
      const response = await updateEzeeNervousSystemState(instanceUniqueId, {
        enabled: false,
        emergencyStop,
        reason: emergencyStop ? emergencyReason.trim() : null,
      });
      setNervousSystem({ ...EMPTY_NERVOUS_SYSTEM, ...(response.state || {}) });
      if (!emergencyStop) setEmergencyReason('');
      setNotice({
        tone: 'success',
        message: emergencyStop
          ? 'Emergency stop enabled. New Ezee triggers, attention scans, and queued job execution are paused.'
          : 'Emergency stop cleared; Ezee remains OFF until deliberately activated.',
      });
    } catch (error: any) {
      setNotice({
        tone: 'error',
        message: error?.message || 'Unable to update the Ezee nervous system.',
      });
    } finally {
      setNervousSaving(false);
    }
  };

  const setNervousSystemEnabled = async (enabled: boolean) => {
    if (!instanceUniqueId || nervousLoading || nervousSaving) return;
    if (enabled && !runtimeArmed) {
      setNotice({ tone: 'info', message: 'Ezee is installed but disarmed. A developer must set EZEE_RUNTIME_ENABLED=true on the backend before activation.' });
      return;
    }
    if (enabled && !window.confirm('Activate Ezee master power for this instance? Capabilities and background workers remain independently OFF.')) return;
    setNervousSaving(true);
    setNotice(null);
    try {
      const response = await updateEzeeNervousSystemState(instanceUniqueId, { enabled });
      setNervousSystem({ ...EMPTY_NERVOUS_SYSTEM, ...(response.state || {}) });
      setNotice({
        tone: 'success',
        message: enabled ? 'Ezee nervous system enabled.' : 'Ezee nervous system disabled.',
      });
    } catch (error: any) {
      setNotice({
        tone: 'error',
        message: error?.message || 'Unable to update the Ezee nervous system.',
      });
    } finally {
      setNervousSaving(false);
    }
  };

  const handleKnowledgeSync = async () => {
    if (!instanceUniqueId || knowledgeSyncing) return;
    setKnowledgeSyncing(true);
    setNotice(null);
    try {
      const response = await syncEzeeKnowledge(instanceUniqueId);
      setKnowledgeStatus({ ...EMPTY_KNOWLEDGE_STATUS, ...(response.status || {}) });
      setNotice({
        tone: 'success',
        message: `Ezee knowledge synchronized. ${response.sync?.indexed || 0} document(s) reindexed; ${response.sync?.unchanged || 0} unchanged.`,
      });
    } catch (error: any) {
      setNotice({
        tone: 'error',
        message: error?.message || 'Unable to synchronize Ezee knowledge.',
      });
    } finally {
      setKnowledgeSyncing(false);
    }
  };

  const handleTest = async () => {
    if (!instanceUniqueId) return;
    setTesting(true);
    setNotice(null);
    try {
      const response = await testEzeeAiConnection(instanceUniqueId);
      if (Array.isArray(response.models)) setModels(response.models);
      setNotice({
        tone: 'success',
        message: response.message || 'Ezee AI provider connection succeeded.',
      });
    } catch (error: any) {
      setNotice({
        tone: 'error',
        message: error?.message || 'Ezee AI provider connection failed.',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="ezee-admin-page">
      <section className="ezee-admin-hero">
        <div>
          <div className="ezee-admin-eyebrow">
            <FiShield aria-hidden="true" />
            Internal Elite Core administration
          </div>
          <h1>Ezee AI</h1>
          <p>
            Configure the private inference provider used by Ezee AI. These controls live only
            in Command Centre and are not exposed in the customer-facing Elite Core settings UI.
          </p>
        </div>
        <div className="ezee-admin-hero__badge">
          <FiCpu aria-hidden="true" />
          MCP-backed
        </div>
      </section>

      <section className={`ezee-admin-card ezee-nervous-card ${nervousSystem.emergencyStop ? 'ezee-nervous-card--stopped' : ''}`}>
        <div className="ezee-admin-card__header">
          <div>
            <div className="ezee-admin-eyebrow">
              <FiAlertTriangle aria-hidden="true" />
              Emergency override
            </div>
            <h2>Ezee nervous system</h2>
            <p>
              Instance master power governs all Ezee operations, including Brain.
              Background processing additionally requires a developer-controlled deployment switch.
            </p>
          </div>
          <div className={`ezee-nervous-state ${nervousSystem.emergencyStop ? 'stopped' : runtimeArmed && nervousSystem.enabled ? 'active' : 'disabled'}`}>
            {nervousLoading
              ? 'Loading…'
              : nervousSystem.emergencyStop
                ? 'EMERGENCY STOP'
                : !runtimeArmed
                  ? 'DEPLOYMENT DISARMED'
                  : nervousSystem.enabled
                    ? 'ACTIVE'
                    : 'DISABLED'}
          </div>
        </div>

        <p role="status" className="ezee-admin-subnote">
          Deployment runtime: <strong>{runtimeArmed ? 'Armed by developer' : 'OFF — cold start'}</strong>.
          Background workers: <strong>{backgroundWorkersArmed ? 'Armed by developer' : 'OFF — no scheduled Ezee jobs'}</strong>.
        </p>
        <div className="ezee-admin-summary ezee-nervous-queue">
          <div>
            <span>Event queue</span>
            <strong>{eventQueue.pending} pending</strong>
          </div>
          <div>
            <span>Processing</span>
            <strong>{eventQueue.processing}</strong>
          </div>
          <div>
            <span>Retry</span>
            <strong>{eventQueue.retry}</strong>
          </div>
          <div>
            <span>Failed</span>
            <strong>{eventQueue.failed}</strong>
          </div>
        </div>

        <div className="ezee-nervous-controls">
          <label className="ezee-admin-switch">
            <input
              type="checkbox"
              checked={nervousSystem.enabled}
              disabled={nervousLoading || nervousSaving || nervousSystem.emergencyStop || (!runtimeArmed && !nervousSystem.enabled)}
              onChange={(event) => setNervousSystemEnabled(event.target.checked)}
            />
            <span>
              <strong>Nervous system enabled</strong>
              <small>
                Controls all Ezee operations, subject to independent deployment and per-capability breakers.
              </small>
            </span>
          </label>

          <div className="ezee-emergency-panel">
            <div>
              <strong>Emergency stop</strong>
              <p>
                Immediately prevents new trigger-driven jobs, pauses attention scanning, and prevents queued AI jobs from being claimed by the worker.
                Existing Core records are never changed by this control.
              </p>
            </div>

            {!nervousSystem.emergencyStop ? (
              <>
                <label className="ezee-admin-field">
                  <span>Reason for emergency stop</span>
                  <textarea
                    value={emergencyReason}
                    onChange={(event) => setEmergencyReason(event.target.value)}
                    placeholder="Example: Paused while investigating unexpected AI job behaviour"
                    rows={3}
                  />
                </label>
                <button
                  type="button"
                  className="danger"
                  disabled={nervousSaving || nervousLoading || !instanceUniqueId}
                  onClick={() => setEmergencyStop(true)}
                >
                  <FiAlertTriangle aria-hidden="true" />
                  {nervousSaving ? 'Stopping…' : 'Emergency stop nervous system'}
                </button>
              </>
            ) : (
              <div className="ezee-emergency-stopped">
                <p>
                  <strong>Reason:</strong> {nervousSystem.reason || 'No reason supplied'}
                </p>
                <p>
                  <strong>Stopped:</strong>{' '}
                  {nervousSystem.stoppedAt ? new Date(nervousSystem.stoppedAt).toLocaleString() : 'Unknown'}
                </p>
                <button
                  type="button"
                  className="primary"
                  disabled={nervousSaving || nervousLoading}
                  onClick={() => setEmergencyStop(false)}
                >
                  {nervousSaving ? 'Clearing…' : 'Clear emergency stop (keep Ezee OFF)'}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="ezee-admin-card ezee-rd-card">
        <div className="ezee-admin-card__header">
          <div>
            <div className="ezee-admin-eyebrow">
              <FiTool aria-hidden="true" />
              Continuous improvement
            </div>
            <h2>Ezee R&amp;D</h2>
            <p>
              Ezee records corrections, failures and repeated weak patterns, turns repeated evidence
              into reviewable recommendations, and verifies whether accepted improvements actually work.
            </p>
          </div>
          <div className="ezee-admin-card__actions">
            <button
              type="button"
              className="secondary"
              disabled={!instanceUniqueId || improvementLoading}
              onClick={() => handleRunEvaluations()}
            >
              <FiPlay aria-hidden="true" />
              Run eval suite
            </button>
            <button
              type="button"
              className="primary"
              disabled={!instanceUniqueId || improvementLoading}
              onClick={handleRunImprovementCycle}
            >
              <FiRefreshCw aria-hidden="true" />
              {improvementLoading ? 'Reflecting…' : 'Run reflection'}
            </button>
          </div>
        </div>

        <div className="ezee-admin-summary">
          <div>
            <span>Observations</span>
            <strong>{improvementLoading ? '…' : improvementDashboard.openObservations}</strong>
          </div>
          <div>
            <span>Ready for review</span>
            <strong>{improvementLoading ? '…' : improvementDashboard.readyForReview}</strong>
          </div>
          <div>
            <span>Verified</span>
            <strong>{improvementLoading ? '…' : improvementDashboard.verified}</strong>
          </div>
          <div>
            <span>Eval quality</span>
            <strong>
              {improvementDashboard.latestEvaluation
                ? `${Math.round(Number(improvementDashboard.latestEvaluation.score || 0) * 100)}%`
                : 'No run yet'}
            </strong>
          </div>
          <div>
            <span>R&D health</span>
            <strong>
              {improvementDashboard.healthScore == null
                ? 'Building baseline'
                : `${Math.round(Number(improvementDashboard.healthScore))}%`}
            </strong>
          </div>
          <div>
            <span>Acceptance</span>
            <strong>
              {improvementDashboard.acceptanceRate == null
                ? 'No reviews yet'
                : `${Math.round(Number(improvementDashboard.acceptanceRate))}%`}
            </strong>
          </div>
        </div>

        <div className="ezee-rd-autonomy">
          <div className="ezee-admin-section-title">
            <div>
              <h3>Improvement autonomy</h3>
              <p>
                Recommend is the current operating mode. Ezee can diagnose, simulate, and prepare exact implementation
                proposals, but application changes remain manually implemented and reviewed for now.
              </p>
            </div>
            <button
              type="button"
              className="ezee-rd-save"
              disabled={improvementSaving}
              onClick={handleSaveImprovementAutonomy}
            >
              <FiSave aria-hidden="true" />
              {improvementSaving ? 'Saving…' : 'Save R&D settings'}
            </button>
          </div>

          <div className="ezee-admin-grid">
            <label className="ezee-admin-field">
              <span>Autonomy mode</span>
              <select
                value={improvementAutonomy.mode}
                onChange={(event) =>
                  setImprovementAutonomy((current) => ({
                    ...current,
                    mode: event.target.value as EzeeImprovementAutonomySettings['mode'],
                  }))
                }
              >
                <option value="observe">Observe only</option>
                <option value="recommend">Recommend</option>
                <option value="simulate">Recommend + simulate</option>
                <option value="prepare">Prepare implementation proposals</option>
                <option value="auto_safe" disabled>Auto-safe application (future)</option>
                <option value="engineering_proposal">Engineering proposal mode</option>
              </select>
            </label>
            <label className="ezee-admin-field">
              <span>Occurrences before recommendation</span>
              <input
                type="number"
                min={1}
                max={20}
                value={improvementAutonomy.minRecommendationOccurrences}
                onChange={(event) =>
                  setImprovementAutonomy((current) => ({
                    ...current,
                    minRecommendationOccurrences: Number(event.target.value || 1),
                  }))
                }
              />
            </label>
            <label className="ezee-admin-field">
              <span>Auto-safe minimum confidence</span>
              <input
                type="number"
                min={0.5}
                max={1}
                step={0.01}
                value={improvementAutonomy.minAutoSafeConfidence}
                onChange={(event) =>
                  setImprovementAutonomy((current) => ({
                    ...current,
                    minAutoSafeConfidence: Number(event.target.value || 0.95),
                  }))
                }
              />
            </label>
            <div className="ezee-rd-toggle-grid">
              <label className="ezee-admin-switch">
                <input
                  type="checkbox"
                  checked={improvementAutonomy.reflectionEnabled}
                  onChange={(event) =>
                    setImprovementAutonomy((current) => ({
                      ...current,
                      reflectionEnabled: event.target.checked,
                    }))
                  }
                />
                <span>
                  <strong>Reflection enabled</strong>
                  <small>Capture corrections, failures and recurring weaknesses.</small>
                </span>
              </label>
              <label className="ezee-admin-switch">
                <input
                  type="checkbox"
                  checked={improvementAutonomy.evaluationsEnabled}
                  onChange={(event) =>
                    setImprovementAutonomy((current) => ({
                      ...current,
                      evaluationsEnabled: event.target.checked,
                    }))
                  }
                />
                <span>
                  <strong>Regression evals enabled</strong>
                  <small>Turn real corrections into persistent quality cases.</small>
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="ezee-admin-divider" />

        <div className="ezee-admin-section-title">
          <div>
            <h3>Recommendations</h3>
            <p>
              Evidence-backed proposals only. Review feedback is retained and becomes future R&amp;D context.
            </p>
          </div>
          <span className="ezee-admin-status">{recommendations.length} total</span>
        </div>

        <div className="ezee-rd-list">
          {recommendations.length ? recommendations.map((recommendation) => {
            const confidence = Math.round(Number(recommendation.confidence || 0) * 100);
            const evidence = evidenceByRecommendation[recommendation.id] || [];
            const plan = recommendation.proposedChange?.implementationPlan;
            return (
              <article className="ezee-rd-recommendation" key={recommendation.id}>
                <div className="ezee-rd-recommendation__top">
                  <div>
                    <div className="ezee-rd-badges">
                      <span>{recommendation.status.replace('_', ' ')}</span>
                      <span>{confidence}% confidence</span>
                      <span>{recommendation.impact} impact</span>
                      <span>{recommendation.risk} risk</span>
                      <span>{recommendation.occurrenceCount} occurrence{recommendation.occurrenceCount === 1 ? '' : 's'}</span>
                    </div>
                    <h4>{recommendation.title}</h4>
                    <p>{recommendation.problem}</p>
                  </div>
                  <div className="ezee-rd-category">{recommendation.category.replace(/_/g, ' ')}</div>
                </div>

                {recommendation.rootCauseHypothesis ? (
                  <div className="ezee-rd-detail">
                    <strong>Root-cause hypothesis</strong>
                    <p>{recommendation.rootCauseHypothesis}</p>
                  </div>
                ) : null}

                {recommendation.proposedChange?.summary ? (
                  <div className="ezee-rd-detail">
                    <strong>Recommended improvement</strong>
                    <p>{recommendation.proposedChange.summary}</p>
                  </div>
                ) : null}

                {plan ? (
                  <details className="ezee-rd-details">
                    <summary>Prepared implementation proposal</summary>
                    <div className="ezee-rd-plan">
                      <p><strong>{plan.prTitle || 'Implementation plan'}</strong></p>
                      <p>{plan.implementationSummary}</p>
                      {Array.isArray(plan.affectedFiles) && plan.affectedFiles.length ? (
                        <div><strong>Affected files</strong><ul>{plan.affectedFiles.map((item: string) => <li key={item}>{item}</li>)}</ul></div>
                      ) : null}
                      {Array.isArray(plan.tests) && plan.tests.length ? (
                        <div><strong>Required tests</strong><ul>{plan.tests.map((item: string, index: number) => <li key={index}>{item}</li>)}</ul></div>
                      ) : null}
                      {Array.isArray(plan.safetyChecks) && plan.safetyChecks.length ? (
                        <div><strong>Safety checks</strong><ul>{plan.safetyChecks.map((item: string, index: number) => <li key={index}>{item}</li>)}</ul></div>
                      ) : null}
                    </div>
                  </details>
                ) : null}

                {recommendation.simulationSummary ? (
                  <div className={`ezee-rd-result ${recommendation.simulationSummary.pass ? 'pass' : 'warn'}`}>
                    <strong>Simulation: {recommendation.simulationSummary.pass ? 'PASS' : 'REVIEW'}</strong>
                    <span>{recommendation.simulationSummary.notes || 'Historical evidence simulation completed.'}</span>
                  </div>
                ) : null}

                {recommendation.verificationSummary ? (
                  <div className={`ezee-rd-result ${recommendation.verificationSummary.outcome === 'verified' ? 'pass' : recommendation.verificationSummary.outcome === 'regressed' ? 'fail' : 'warn'}`}>
                    <strong>Verification: {recommendation.verificationSummary.outcome}</strong>
                    <span>
                      Before {recommendation.verificationSummary.beforeCount} → after {recommendation.verificationSummary.afterCount}
                      {' '}({recommendation.verificationSummary.reductionPct}% reduction)
                    </span>
                  </div>
                ) : null}

                {evidence.length ? (
                  <details className="ezee-rd-details" open>
                    <summary>Evidence ({evidence.length})</summary>
                    <div className="ezee-rd-evidence">
                      {evidence.slice(0, 12).map((observation) => (
                        <div key={observation.id}>
                          <span>{observation.severity}</span>
                          <p>{observation.summary}</p>
                          <small>{new Date(observation.occurredAt).toLocaleString()} · {observation.sourceType || 'Ezee'}</small>
                        </div>
                      ))}
                    </div>
                  </details>
                ) : null}

                <label className="ezee-admin-field ezee-rd-review-note">
                  <span>Reviewer note / reason</span>
                  <textarea
                    rows={2}
                    value={reviewReasonByRecommendation[recommendation.id] || ''}
                    onChange={(event) =>
                      setReviewReasonByRecommendation((current) => ({
                        ...current,
                        [recommendation.id]: event.target.value,
                      }))
                    }
                    placeholder="Why you approve/reject it, or what Ezee should change."
                  />
                </label>

                <div className="ezee-rd-actions">
                  <button type="button" onClick={() => handleLoadEvidence(recommendation)}>
                    <FiEye aria-hidden="true" /> Evidence
                  </button>
                  <button type="button" onClick={() => handleSimulateRecommendation(recommendation)} disabled={improvementActionId === recommendation.id}>
                    <FiPlay aria-hidden="true" /> Simulate
                  </button>
                  <button type="button" onClick={() => handlePrepareRecommendation(recommendation)} disabled={improvementActionId === recommendation.id}>
                    <FiTool aria-hidden="true" /> Prepare
                  </button>
                  <button type="button" className="approve" onClick={() => handleReviewRecommendation(recommendation, 'approved')} disabled={improvementActionId === recommendation.id}>
                    <FiCheckCircle aria-hidden="true" /> Approve
                  </button>
                  <button type="button" onClick={() => handleReviewRecommendation(recommendation, 'needs_changes')} disabled={improvementActionId === recommendation.id}>
                    Needs changes
                  </button>
                  <button type="button" className="reject" onClick={() => handleReviewRecommendation(recommendation, 'rejected')} disabled={improvementActionId === recommendation.id}>
                    <FiXCircle aria-hidden="true" /> Reject
                  </button>
                  <button type="button" onClick={() => handleReviewRecommendation(recommendation, 'implemented')} disabled={improvementActionId === recommendation.id}>
                    Mark implemented
                  </button>
                  {recommendation.implementedAt ? (
                    <button type="button" onClick={() => handleVerifyRecommendation(recommendation)} disabled={improvementActionId === recommendation.id}>
                      Verify
                    </button>
                  ) : null}
                  <button type="button" onClick={() => handleRunEvaluations(recommendation.id)} disabled={improvementLoading}>
                    Run linked evals
                  </button>
                </div>
              </article>
            );
          }) : (
            <div className="ezee-rd-empty">
              No improvement recommendations yet. Ezee will create them after repeated evidence reaches the configured threshold.
            </div>
          )}
        </div>

        <div className="ezee-admin-divider" />

        <div className="ezee-admin-section-title">
          <div>
            <h3>Ezee quality / evaluations</h3>
            <p>Real corrections become regression cases so model, prompt, knowledge and tool changes can be measured.</p>
          </div>
          <span className="ezee-admin-status">{evaluationCases.length} cases</span>
        </div>

        <div className="ezee-rd-eval-grid">
          <div>
            <strong>Latest run</strong>
            {evaluationRuns[0] ? (
              <>
                <div className="ezee-rd-score">
                  {Math.round(Number(evaluationRuns[0].score || 0) * 100)}%
                </div>
                <p>
                  {evaluationRuns[0].passedCases}/{evaluationRuns[0].totalCases} passed ·
                  {' '}{evaluationRuns[0].provider || 'provider'} / {evaluationRuns[0].model || 'model'}
                </p>
              </>
            ) : <p>No regression run yet.</p>}
          </div>
          <div>
            <strong>Quality gate</strong>
            <p>
              Use the evaluation score plus verified recommendation outcomes—not perceived cleverness—to decide
              when Ezee earns more autonomy.
            </p>
          </div>
        </div>
      </section>

      <section className="ezee-admin-card">
        <div className="ezee-admin-card__header">
          <div>
            <div className="ezee-admin-eyebrow">
              <FiCpu aria-hidden="true" />
              Retrieval layer
            </div>
            <h2>Company knowledge</h2>
            <p>
              Ezee searches these indexed Elite Core workflow and SOP documents alongside live Core records.
              Bundled documents are synchronized automatically when the AI worker starts.
            </p>
          </div>
          <div className="ezee-admin-card__actions">
            <button
              type="button"
              className="secondary"
              onClick={handleKnowledgeSync}
              disabled={!instanceUniqueId || knowledgeLoading || knowledgeSyncing}
            >
              <FiRefreshCw aria-hidden="true" />
              {knowledgeSyncing ? 'Syncing knowledge…' : 'Sync knowledge'}
            </button>
          </div>
        </div>

        <div className="ezee-admin-summary">
          <div>
            <span>Documents</span>
            <strong>{knowledgeLoading ? '…' : knowledgeStatus.documents}</strong>
          </div>
          <div>
            <span>Enabled</span>
            <strong>{knowledgeLoading ? '…' : knowledgeStatus.enabledDocuments}</strong>
          </div>
          <div>
            <span>Search chunks</span>
            <strong>{knowledgeLoading ? '…' : knowledgeStatus.chunks}</strong>
          </div>
          <div>
            <span>Last indexed</span>
            <strong>
              {knowledgeLoading
                ? '…'
                : knowledgeStatus.latestIndexedAt
                  ? new Date(knowledgeStatus.latestIndexedAt).toLocaleString()
                  : 'Not indexed yet'}
            </strong>
          </div>
        </div>

        <div className="ezee-admin-security" style={{ marginTop: 18 }}>
          <FiShield aria-hidden="true" />
          <div>
            <h3>Knowledge is guidance, live Core is fact</h3>
            <p>
              Workflow documents explain how Elite Core is intended to operate. Ezee still verifies current
              statuses, amounts, assignments, proofs, receiving, billing, and payment state from live MCP tools.
            </p>
          </div>
        </div>
      </section>

      <section className="ezee-admin-card">
        <div className="ezee-admin-card__header">
          <div>
            <h2>Provider configuration</h2>
            <p>
              Choose which Elite Core instance uses the provider, model, and credentials below.
            </p>
          </div>
          <div className="ezee-admin-card__actions">
            <button type="button" className="secondary" onClick={handleLoadModels} disabled={!instanceUniqueId || loadingModels || loadingSettings}>
              <FiRefreshCw aria-hidden="true" />
              {loadingModels ? 'Loading models…' : 'Load models'}
            </button>
            <button type="button" className="secondary" onClick={handleTest} disabled={!instanceUniqueId || testing || loadingSettings}>
              <FiZap aria-hidden="true" />
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button type="button" className="primary" onClick={handleSave} disabled={!instanceUniqueId || saving || loadingSettings}>
              <FiSave aria-hidden="true" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {notice ? (
          <div className={`ezee-admin-notice ezee-admin-notice--${notice.tone}`}>
            {notice.message}
          </div>
        ) : null}

        <div className="ezee-admin-grid">
          <label className="ezee-admin-field">
            <span>Elite Core instance</span>
            <select
              value={instanceUniqueId}
              onChange={(event) => setInstanceUniqueId(event.target.value)}
              disabled={loadingInstances}
            >
              <option value="">
                {loadingInstances ? 'Loading instances…' : 'Select an instance'}
              </option>
              {instances.map((instance) => (
                <option key={instance.id} value={instance.instanceUniqueId || ''}>
                  {instance.displayName} ({instance.instanceUniqueId})
                </option>
              ))}
            </select>
          </label>

          <label className="ezee-admin-field">
            <span>Provider</span>
            <select
              value={settings.provider}
              onChange={(event) => handleProviderChange(event.target.value)}
              disabled={loadingSettings}
            >
              <option value="digitalocean">DigitalOcean Inference</option>
              <option value="openai">OpenAI</option>
            </select>
          </label>

          <label className="ezee-admin-field">
            <span>Active mode</span>
            <select
              value={settings.mode}
              onChange={(event) => update('mode', event.target.value as 'test' | 'live')}
              disabled={loadingSettings}
            >
              <option value="live">Live</option>
              <option value="test">Test</option>
            </select>
          </label>

          <label className="ezee-admin-field ezee-admin-field--wide">
            <span>Base URL</span>
            <input
              type="text"
              value={settings.baseUrl || ''}
              onChange={(event) => update('baseUrl', event.target.value)}
              placeholder={
                settings.provider === 'digitalocean'
                  ? DIGITALOCEAN_BASE_URL
                  : 'https://api.openai.com/v1'
              }
              disabled={loadingSettings}
            />
          </label>
        </div>

        <div className="ezee-admin-divider" />

        <div className="ezee-admin-section-title">
          <div>
            <h3>Live credentials</h3>
            <p>Used by Ezee AI in production.</p>
          </div>
          {settings.hasLiveApiKey ? (
            <span className="ezee-admin-status">Configured {settings.liveApiKeyMasked}</span>
          ) : null}
        </div>

        <div className="ezee-admin-grid">
          <label className="ezee-admin-field">
            <span>{settings.provider === 'digitalocean' ? 'Live model access key' : 'Live API key'}</span>
            <input
              type="password"
              value={settings.liveApiKey || ''}
              onChange={(event) => update('liveApiKey', event.target.value)}
              placeholder={settings.hasLiveApiKey ? 'Saved — enter a new value to replace' : 'Enter provider key'}
              autoComplete="new-password"
            />
          </label>

          <label className="ezee-admin-field">
            <span>Live model</span>
            <input
              type="text"
              list="ezee-provider-models"
              value={settings.liveModel || ''}
              onChange={(event) => update('liveModel', event.target.value)}
              placeholder="Select or paste exact model id"
            />
          </label>
        </div>

        <div className="ezee-admin-divider" />

        <div className="ezee-admin-section-title">
          <div>
            <h3>Test credentials</h3>
            <p>Optional isolated provider/model configuration for validation.</p>
          </div>
          {settings.hasTestApiKey ? (
            <span className="ezee-admin-status">Configured {settings.testApiKeyMasked}</span>
          ) : null}
        </div>

        <div className="ezee-admin-grid">
          <label className="ezee-admin-field">
            <span>{settings.provider === 'digitalocean' ? 'Test model access key' : 'Test API key'}</span>
            <input
              type="password"
              value={settings.testApiKey || ''}
              onChange={(event) => update('testApiKey', event.target.value)}
              placeholder={settings.hasTestApiKey ? 'Saved — enter a new value to replace' : 'Enter provider key'}
              autoComplete="new-password"
            />
          </label>

          <label className="ezee-admin-field">
            <span>Test model</span>
            <input
              type="text"
              list="ezee-provider-models"
              value={settings.testModel || ''}
              onChange={(event) => update('testModel', event.target.value)}
              placeholder="Select or paste exact model id"
            />
          </label>
        </div>

        <datalist id="ezee-provider-models">
          {models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.ownedBy || ''}
            </option>
          ))}
        </datalist>

        <label className="ezee-admin-switch">
          <input
            type="checkbox"
            checked={settings.useEnvFallback}
            onChange={(event) => update('useEnvFallback', event.target.checked)}
          />
          <span>
            <strong>Allow server environment fallback</strong>
            <small>
              If no active mode key is saved, Elite Core may use the provider-specific server
              environment credential.
            </small>
          </span>
        </label>

        <div className="ezee-admin-summary">
          <div>
            <span>Selected instance</span>
            <strong>{selectedInstance?.displayName || '—'}</strong>
          </div>
          <div>
            <span>Provider</span>
            <strong>{settings.provider === 'digitalocean' ? 'DigitalOcean' : 'OpenAI'}</strong>
          </div>
          <div>
            <span>Active model</span>
            <strong>{activeModel || 'Not selected'}</strong>
          </div>
          <div>
            <span>Active key</span>
            <strong>{activeKeyMask || 'Not configured'}</strong>
          </div>
        </div>
      </section>

      <section className="ezee-admin-security">
        <FiShield aria-hidden="true" />
        <div>
          <h3>Security boundary</h3>
          <p>
            Provider credentials are stored by Elite Core and remain server-side. Ezee uses the
            shared MCP registry for live Core data; the model provider is never given an Elite Core
            administrator token or reusable MCP credential.
          </p>
        </div>
      </section>
    </div>
  );
};

export default EzeeAiAdminPage;
